import map from 'lodash/map'
import sumBy from 'lodash/sumBy'
import { FormBuilderContext } from 'pages/Contexts/FormBuilderContext'
import * as React from 'react'
import { Droppable } from 'react-beautiful-dnd'
import { SELECTION_TYPE } from 'utils/FormBuilder/ConstantsFormBuilder'

import BuilderAddButton from './BuilderAddButton'
import BuilderQuestion from './BuilderQuestion'

function BuilderRow({ rowId, builderSectionWidth }) {
  const {
    isReordering,
    reorderSourceRow,
    reorderColumns,
    rowToScroll,
    setRowToScroll,
    questions: questionsObj,
    getSection,
    getPage,
  } = React.useContext(FormBuilderContext)

  const [hasAddQuestionButton, setHasAddQuestionButton] = React.useState(false)

  const rowContainerRef = React.useRef()

  React.useEffect(() => {
    if (rowToScroll === rowId) {
      rowContainerRef.current.scrollIntoView({ behavior: 'smooth' })
      setRowToScroll(null)
    }
  }, [setRowToScroll, rowToScroll, rowId])

  const questionsArray = Object.keys(questionsObj).map((key) => ({
    id: key,
    ...questionsObj[key],
  }))

  const rowQuestions = questionsArray
    .filter((q) => q.row === rowId)
    .sort((a, b) => a.order - b.order)

  const totalQuestionColumns = sumBy(rowQuestions, (row) =>
    Number(row?.display_columns ?? 0)
  )

  const remainingColumns =
    totalQuestionColumns === 0 ? 6 : 12 - totalQuestionColumns

  React.useEffect(() => {
    setHasAddQuestionButton(remainingColumns > 0)
  }, [remainingColumns])

  const isDropDisabled = React.useCallback(() => {
    const basicDropDisabled =
      reorderSourceRow !== rowId && rowQuestions.length >= 12
    if (basicDropDisabled) return true

    if (!reorderSourceRow) return false

    const targetQuestion = rowQuestions[0]
    if (!targetQuestion) return false

    const targetSection = getSection(targetQuestion.section)
    if (!targetSection) return false

    const targetPage = getPage(targetSection.page)
    const targetIsRepeatSection = Boolean(targetPage?.repeat_section)

    const sourceQuestion = questionsArray.find(
      (q) => q.row === reorderSourceRow
    )
    if (!sourceQuestion) return false

    const sourceSection = getSection(sourceQuestion.section)
    if (!sourceSection) return false

    const sourcePage = getPage(sourceSection.page)
    const sourceIsRepeatSection = Boolean(sourcePage?.repeat_section)

    return targetIsRepeatSection !== sourceIsRepeatSection
  }, [
    reorderSourceRow,
    rowId,
    rowQuestions,
    questionsArray,
    getSection,
    getPage,
  ])

  return (
    <div ref={rowContainerRef}>
      <Droppable
        direction="horizontal"
        droppableId={rowId}
        isDropDisabled={isDropDisabled()}
        type="TASK"
      >
        {(provided, snapshot) => (
          <div
            className="-mx-1 grid grid-cols-1 sm:grid-cols-12"
            ref={provided.innerRef}
            style={{
              borderWidth: 1,
              borderRadius: 4,
              borderColor: snapshot.isDraggingOver ? '#1d8cf8' : 'transparent',
            }}
            {...provided.droppableProps}
          >
            {map(rowQuestions, (question, i) => (
              <BuilderQuestion
                builderSectionWidth={builderSectionWidth}
                hasMakeBiggerOption={hasAddQuestionButton}
                index={i}
                isDragDisabled={
                  isReordering &&
                  reorderSourceRow !== rowId &&
                  remainingColumns < reorderColumns
                }
                key={question?.id ?? i}
                questionId={question?.id}
                totalQuestionColumns={totalQuestionColumns}
              />
            ))}
            {!isReordering && hasAddQuestionButton && (
              <BuilderAddButton
                isSelectableItem
                colSpan={remainingColumns}
                containerClassNames="px-1"
                id={`${rowId}_${remainingColumns}`}
                key={`+_${rowId}`}
                spacingTop={0}
                type={SELECTION_TYPE.QUESTION_ADD}
              >
                +
              </BuilderAddButton>
            )}
            <div style={{ maxHeight: 0 }}>{provided.placeholder}</div>
          </div>
        )}
      </Droppable>
    </div>
  )
}

export default BuilderRow
